import React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
  SimpleForm,
  TextInput,
  SelectInput,
  RadioButtonGroupInput,
  BooleanInput,
  FileInput,
  FileField,
  useRecordContext,
  useTranslate,
  useResourceContext,
  required,
  maxLength,
  minValue,
} from "react-admin";
import {
  Box,
  Card,
  CardContent,
  Switch,
  Grid,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { MyToolbar } from "@/components/forms";
import { PhoneNumberInput } from "@/components/common";
import { MetaFieldHelp } from "./MetaFieldHelp";
import { SendGridFieldHelp } from "./SendGridFieldHelp";
import { ChannelFieldHelp } from "./ChannelFieldHelp";
import { ProviderRail, type ProviderRailGroup } from "./ProviderRail";
import { useIsEditMode } from "@/hooks/useIsEditMode";
import { parseNumericInput } from "@/utils/validation";
import {
  EMAIL_PROVIDER_CHOICES,
  EMAIL_SECURITY_CHOICES,
  MAX_PROVIDER_NAME_LENGTH,
  PROVIDER_STATUS_CHOICES,
  WHATSAPP_TRANSPORT_CHOICES,
  TEAM_CHAT_PLATFORMS,
} from "../constants";
import type { ProviderFormValues } from "../types/provider";

const useSensitiveHelperText = (): string | undefined => {
  const translate = useTranslate();
  const isEdit = useIsEditMode();
  return isEdit
    ? translate("resources.providers.help.sensitiveOnEdit")
    : undefined;
};

const useValidationMessages = () => {
  const translate = useTranslate();
  const fieldLabel = (key: string): string =>
    translate(`resources.providers.fields.${key}`);
  const requiredMsg = (key: string): string =>
    translate("resources.providers.validation.required", {
      field: fieldLabel(key),
    });
  return {
    requiredMsg,
    maxLengthMsg: (key: string, max: number): string =>
      translate("resources.providers.validation.maxLength", {
        field: fieldLabel(key),
        max,
      }),
    minValueMsg: (key: string, min: number): string =>
      translate("resources.providers.validation.minValue", {
        field: fieldLabel(key),
        min,
      }),
    positiveMsg: (key: string): string =>
      translate("resources.providers.validation.positive", {
        field: fieldLabel(key),
      }),
    requiredFor: (source: string): string =>
      requiredMsg(source.split(".").pop() ?? source),
  };
};

const ProviderToggleHeader: React.FC<{ source: string; title: string }> = ({
  source,
  title,
}) => {
  const { control } = useFormContext();
  const translate = useTranslate();
  return (
    <Box sx={{ display: "flex", alignItems: "center", py: 0.5 }}>
      <Controller
        name={source}
        control={control}
        render={({ field }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            <Switch
              checked={Boolean(field.value)}
              onChange={(e) => field.onChange(e.target.checked)}
              size="small"
              inputProps={{ "aria-label": title }}
            />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: field.value ? "success.main" : "text.disabled",
              }}
            >
              {field.value
                ? translate("resources.providers.channel.active", {
                    _: "Active",
                  })
                : translate("resources.providers.channel.blocked", {
                    _: "Blocked",
                  })}
            </Typography>
          </Box>
        )}
      />
    </Box>
  );
};

const panelSx = {
  border: 1,
  borderColor: "divider",
  borderRadius: 1,
  p: { xs: 1.5, sm: 3 },
  mt: 2,
  mb: 3,
};

const BasicSection: React.FC = () => {
  const translate = useTranslate();
  const isEdit = useIsEditMode();
  const { requiredMsg, maxLengthMsg, minValueMsg, positiveMsg } =
    useValidationMessages();
  const positiveFor = (key: string) => (value: number | string | undefined) =>
    value !== undefined && value !== "" && Number(value) <= 0
      ? positiveMsg(key)
      : undefined;
  return (
    <Card sx={{ border: 1, borderColor: "divider", mb: 3 }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
        <Grid container spacing={{ xs: 1.5, sm: 3 }}>
          <Grid item xs={12} md={6}>
            <TextInput
              source="providerName"
              label={translate("resources.providers.fields.providerName")}
              validate={[
                required(requiredMsg("providerName")),
                maxLength(
                  MAX_PROVIDER_NAME_LENGTH,
                  maxLengthMsg("providerName", MAX_PROVIDER_NAME_LENGTH),
                ),
              ]}
              fullWidth
              autoFocus={!isEdit}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SelectInput
              source="providerStatus"
              label={translate("resources.providers.fields.providerStatus")}
              choices={PROVIDER_STATUS_CHOICES}
              validate={required(requiredMsg("providerStatus"))}
              fullWidth
              sx={{ mt: 0 }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput
              source="priority"
              label={translate("resources.providers.fields.priority")}
              type="number"
              validate={[
                required(requiredMsg("priority")),
                minValue(1, minValueMsg("priority", 1)),
                positiveFor("priority"),
              ]}
              parse={parseNumericInput}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInput
              source="retries"
              label={translate("resources.providers.fields.retries")}
              type="number"
              validate={[
                required(requiredMsg("retries")),
                minValue(1, minValueMsg("retries", 1)),
                positiveFor("retries"),
              ]}
              parse={parseNumericInput}
              fullWidth
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const EmailSection: React.FC = () => {
  const translate = useTranslate();
  const isEdit = useIsEditMode();
  const record = useRecordContext<ProviderFormValues>();
  const emailVariant = useWatch({ name: "email" });
  const sensitiveHelper = useSensitiveHelperText();
  const { requiredFor } = useValidationMessages();
  const oauth2AlreadyConfigured =
    isEdit && record?.email === "emailSmtpViaOauthProvider";
  const apiAlreadyConfigured = isEdit && record?.email === "api";

  return (
    <Card sx={{ border: 1, borderColor: "divider", mb: 3 }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1, sm: 2 },
            flexWrap: "wrap",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {translate("resources.providers.sections.email")}
          </Typography>
          <RadioButtonGroupInput
            source="email"
            label={false}
            choices={EMAIL_PROVIDER_CHOICES}
            row
            helperText={false}
            sx={{
              m: 0,
              flex: { sm: 1 },
              "& .MuiFormGroup-root": {
                gap: { xs: 0.5, sm: 1 },
                flexDirection: { xs: "column", sm: "row" },
                flexWrap: "wrap",
              },
              "& .MuiFormControlLabel-root": {
                marginLeft: 0,
                marginRight: { xs: 0, sm: 2 },
              },
              "& .MuiFormControlLabel-label": {
                fontSize: { xs: "0.875rem", sm: "0.9rem" },
              },
            }}
          />
        </Box>

        {emailVariant === "emailSmtpViaBasicAuthProvider" && (
          <Box sx={panelSx}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              {translate("resources.providers.email.variants.basicAuth")}
            </Typography>
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={12} md={6}>
                <TextInput
                  source="emailSmtpViaBasicAuthProvider.hostname"
                  label={translate("resources.providers.fields.hostname")}
                  validate={required(
                    requiredFor("emailSmtpViaBasicAuthProvider.hostname"),
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  source="emailSmtpViaBasicAuthProvider.port"
                  label={translate("resources.providers.fields.port")}
                  validate={required(
                    requiredFor("emailSmtpViaBasicAuthProvider.port"),
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  source="emailSmtpViaBasicAuthProvider.username"
                  label={translate("resources.providers.fields.username")}
                  validate={required(
                    requiredFor("emailSmtpViaBasicAuthProvider.username"),
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  source="emailSmtpViaBasicAuthProvider.password"
                  label={translate("resources.providers.fields.password")}
                  type="password"
                  validate={required(
                    requiredFor("emailSmtpViaBasicAuthProvider.password"),
                  )}
                  fullWidth
                  helperText={sensitiveHelper}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SelectInput
                  source="emailSmtpViaBasicAuthProvider.securityType"
                  label={translate("resources.providers.fields.securityType")}
                  choices={EMAIL_SECURITY_CHOICES}
                  validate={required(
                    requiredFor("emailSmtpViaBasicAuthProvider.securityType"),
                  )}
                  fullWidth
                  // Match the sibling `TextInput`'s baseline. RA's
                  // `<SelectInput>` adds a default top margin that
                  // pushes it lower than `<TextInput>` in the same row.
                  sx={{ mt: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  source="emailSmtpViaBasicAuthProvider.from"
                  label={translate("resources.providers.fields.from")}
                  validate={required(
                    requiredFor("emailSmtpViaBasicAuthProvider.from"),
                  )}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {emailVariant === "emailSmtpViaOauthProvider" && (
          <Box sx={panelSx}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              {translate("resources.providers.email.variants.oauth2")}
            </Typography>
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={12} md={6}>
                <TextInput
                  source="emailSmtpViaOauthProvider.hostname"
                  label={translate("resources.providers.fields.hostname")}
                  validate={required(
                    requiredFor("emailSmtpViaOauthProvider.hostname"),
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  source="emailSmtpViaOauthProvider.port"
                  label={translate("resources.providers.fields.port")}
                  validate={required(
                    requiredFor("emailSmtpViaOauthProvider.port"),
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  source="emailSmtpViaOauthProvider.serviceAccountEmail"
                  label={translate(
                    "resources.providers.fields.serviceAccountEmail",
                  )}
                  validate={required(
                    requiredFor(
                      "emailSmtpViaOauthProvider.serviceAccountEmail",
                    ),
                  )}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  source="emailSmtpViaOauthProvider.privateKey"
                  label={translate("resources.providers.fields.privateKey")}
                  validate={
                    oauth2AlreadyConfigured
                      ? undefined
                      : required(
                          requiredFor("emailSmtpViaOauthProvider.privateKey"),
                        )
                  }
                  fullWidth
                  helperText={sensitiveHelper}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SelectInput
                  source="emailSmtpViaOauthProvider.securityType"
                  label={translate("resources.providers.fields.securityType")}
                  choices={EMAIL_SECURITY_CHOICES}
                  validate={required(
                    requiredFor("emailSmtpViaOauthProvider.securityType"),
                  )}
                  fullWidth
                  sx={{ mt: 0 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  source="emailSmtpViaOauthProvider.userToImpersonate"
                  label={translate(
                    "resources.providers.fields.userToImpersonate",
                  )}
                  validate={required(
                    requiredFor("emailSmtpViaOauthProvider.userToImpersonate"),
                  )}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {emailVariant === "api" && (
          <Box sx={panelSx}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              {translate("resources.providers.email.variants.api")}
            </Typography>
            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              <Grid item xs={12} md={6}>
                <TextInput
                  source="emailApiProvider.apiEndpoint"
                  label={translate("resources.providers.fields.apiEndpoint")}
                  validate={required(
                    requiredFor("emailApiProvider.apiEndpoint"),
                  )}
                  fullWidth
                  InputProps={{
                    endAdornment: <SendGridFieldHelp field="apiEndpoint" />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  source="emailApiProvider.apiKey"
                  label={translate("resources.providers.fields.apiKey")}
                  validate={
                    apiAlreadyConfigured
                      ? undefined
                      : required(requiredFor("emailApiProvider.apiKey"))
                  }
                  fullWidth
                  helperText={sensitiveHelper}
                  InputProps={{
                    endAdornment: <SendGridFieldHelp field="apiKey" />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  source="emailApiProvider.from"
                  label={translate("resources.providers.fields.from")}
                  validate={required(requiredFor("emailApiProvider.from"))}
                  fullWidth
                  InputProps={{
                    endAdornment: <SendGridFieldHelp field="from" />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextInput
                  source="emailApiProvider.customHeaders"
                  label={translate("resources.providers.fields.customHeaders")}
                  fullWidth
                  InputProps={{
                    endAdornment: <SendGridFieldHelp field="customHeaders" />,
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const SmsSection: React.FC = () => {
  const translate = useTranslate();
  const isEdit = useIsEditMode();
  const record = useRecordContext<ProviderFormValues>();
  const smsEnabled = useWatch({ name: "sms" });
  const sensitiveHelper = useSensitiveHelperText();
  const { requiredFor } = useValidationMessages();
  const smsAlreadyConfigured = isEdit && Boolean(record?.sms);

  return (
    <Card sx={{ border: 1, borderColor: "divider" }}>
      <CardContent
        sx={{
          pt: { xs: 1.5, sm: 3, lg: 0 },
          pb: { xs: 1.5, sm: 3, lg: 0 },
          pl: { xs: 1.5, sm: 3 },
          pr: { xs: 1.5, sm: 3 },
          mb: 0,
        }}
      >
        <ProviderToggleHeader
          source="sms"
          title={translate("resources.providers.sections.sms")}
        />
        <Box>
          {smsEnabled && (
            <Box sx={panelSx}>
              <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="smsProvider.apiEndpoint"
                    label={translate("resources.providers.fields.apiEndpoint")}
                    validate={required(requiredFor("smsProvider.apiEndpoint"))}
                    fullWidth
                    InputProps={{
                      endAdornment: <ChannelFieldHelp id="sms.apiEndpoint" />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="smsProvider.apiKey"
                    label={translate("resources.providers.fields.apiKey")}
                    validate={
                      smsAlreadyConfigured
                        ? undefined
                        : required(requiredFor("smsProvider.apiKey"))
                    }
                    fullWidth
                    helperText={sensitiveHelper}
                    InputProps={{
                      endAdornment: <ChannelFieldHelp id="sms.apiKey" />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="smsProvider.apisecret"
                    label={translate("resources.providers.fields.apisecret")}
                    validate={required(requiredFor("smsProvider.apisecret"))}
                    fullWidth
                    InputProps={{
                      endAdornment: <ChannelFieldHelp id="sms.apisecret" />,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextInput
                    source="smsProvider.senderId"
                    label={translate("resources.providers.fields.senderId")}
                    validate={required(requiredFor("smsProvider.senderId"))}
                    fullWidth
                    InputProps={{
                      endAdornment: <ChannelFieldHelp id="sms.senderId" />,
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const WhatsAppSection: React.FC = () => {
  const translate = useTranslate();
  const isEdit = useIsEditMode();
  const record = useRecordContext<ProviderFormValues>();
  const enabled = useWatch({ name: "whatsApp" });
  const transport = useWatch({ name: "whatsAppProviderType" });
  const sensitiveHelper = useSensitiveHelperText();
  const { requiredFor } = useValidationMessages();

  const isTwilio = transport === "twilio";
  const isMeta = transport === "meta";

  const twilioAlreadyConfigured =
    isEdit && isTwilio && Boolean(record?.whatsappProvider?.id);
  const metaAlreadyConfigured =
    isEdit && isMeta && Boolean(record?.whatsappMetaProvider?.id);

  return (
    <Card sx={{ border: 1, borderColor: "divider" }}>
      <CardContent
        sx={{
          pt: { xs: 1.5, sm: 3, lg: 0 },
          pb: { xs: 1.5, sm: 3, lg: 0 },
          pl: { xs: 1.5, sm: 3 },
          pr: { xs: 1.5, sm: 3 },
          mb: 0,
        }}
      >
        <ProviderToggleHeader
          source="whatsApp"
          title={translate("resources.providers.sections.whatsApp")}
        />
        <Box>
          {enabled && (
            <Box sx={panelSx}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: { xs: "flex-start", sm: "center" },
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 0.5, sm: 2 },
                  flexWrap: "wrap",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {translate("resources.providers.whatsApp.transport")}
                </Typography>
                <RadioButtonGroupInput
                  source="whatsAppProviderType"
                  label={false}
                  choices={WHATSAPP_TRANSPORT_CHOICES}
                  row
                  helperText={false}
                  validate={required(requiredFor("whatsAppProviderType"))}
                  sx={{
                    m: 0,
                    flex: { sm: 1 },
                    "& .MuiFormGroup-root": {
                      gap: { xs: 0.5, sm: 1 },
                      flexDirection: { xs: "column", sm: "row" },
                      flexWrap: "wrap",
                    },
                    "& .MuiFormControlLabel-root": {
                      marginLeft: 0,
                      marginRight: { xs: 0, sm: 2 },
                    },
                    "& .MuiFormControlLabel-label": {
                      fontSize: { xs: "0.875rem", sm: "0.9rem" },
                    },
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 2 }}
              >
                {translate("resources.providers.whatsApp.transportHelper")}
              </Typography>

              {isTwilio && (
                <Box sx={panelSx}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    {translate("resources.providers.whatsApp.variants.twilio")}
                  </Typography>
                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    <Grid item xs={12} md={6}>
                      <TextInput
                        source="whatsappProvider.apiEndpoint"
                        label={translate(
                          "resources.providers.fields.apiEndpoint",
                        )}
                        validate={required(
                          requiredFor("whatsappProvider.apiEndpoint"),
                        )}
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <ChannelFieldHelp id="twilioWa.apiEndpoint" />
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextInput
                        source="whatsappProvider.apiKey"
                        label={translate("resources.providers.fields.apiKey")}
                        validate={
                          twilioAlreadyConfigured
                            ? undefined
                            : required(requiredFor("whatsappProvider.apiKey"))
                        }
                        fullWidth
                        helperText={sensitiveHelper}
                        InputProps={{
                          endAdornment: (
                            <ChannelFieldHelp id="twilioWa.apiKey" />
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextInput
                        source="whatsappProvider.apisecret"
                        label={translate(
                          "resources.providers.fields.apisecret",
                        )}
                        validate={required(
                          requiredFor("whatsappProvider.apisecret"),
                        )}
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <ChannelFieldHelp id="twilioWa.apisecret" />
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <PhoneNumberInput
                        source="whatsappProvider.businessPhoneNumber"
                        label={translate(
                          "resources.providers.fields.businessPhoneNumber",
                        )}
                        validate={required(
                          requiredFor("whatsappProvider.businessPhoneNumber"),
                        )}
                        required
                        endAdornment={
                          <ChannelFieldHelp id="twilioWa.businessPhoneNumber" />
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              {isMeta && (
                <Box sx={panelSx}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 2 }}
                  >
                    {translate("resources.providers.whatsApp.variants.meta")}
                  </Typography>
                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    <Grid item xs={12} md={6}>
                      <TextInput
                        source="whatsappMetaProvider.phoneNumberId"
                        label={translate(
                          "resources.providers.fields.phoneNumberId",
                        )}
                        validate={required(
                          requiredFor("whatsappMetaProvider.phoneNumberId"),
                        )}
                        fullWidth
                        InputProps={{
                          endAdornment: <MetaFieldHelp field="phoneNumberId" />,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextInput
                        source="whatsappMetaProvider.wabaId"
                        label={translate("resources.providers.fields.wabaId")}
                        validate={required(
                          requiredFor("whatsappMetaProvider.wabaId"),
                        )}
                        fullWidth
                        InputProps={{
                          endAdornment: <MetaFieldHelp field="wabaId" />,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextInput
                        source="whatsappMetaProvider.accessToken"
                        label={translate(
                          "resources.providers.fields.accessToken",
                        )}
                        type="password"
                        validate={
                          metaAlreadyConfigured
                            ? undefined
                            : required(
                                requiredFor("whatsappMetaProvider.accessToken"),
                              )
                        }
                        fullWidth
                        InputProps={{
                          endAdornment: <MetaFieldHelp field="accessToken" />,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextInput
                        source="whatsappMetaProvider.graphApiVersion"
                        label={translate(
                          "resources.providers.fields.graphApiVersion",
                        )}
                        validate={required(
                          requiredFor("whatsappMetaProvider.graphApiVersion"),
                        )}
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <MetaFieldHelp field="graphApiVersion" />
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextInput
                        source="whatsappMetaProvider.apiEndpoint"
                        label={translate(
                          "resources.providers.fields.apiEndpoint",
                        )}
                        validate={required(
                          requiredFor("whatsappMetaProvider.apiEndpoint"),
                        )}
                        fullWidth
                        InputProps={{
                          endAdornment: <MetaFieldHelp field="apiEndpoint" />,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <PhoneNumberInput
                        source="whatsappMetaProvider.metaBusinessPhoneNumber"
                        label={translate(
                          "resources.providers.fields.metaBusinessPhoneNumber",
                        )}
                        validate={required(
                          requiredFor(
                            "whatsappMetaProvider.metaBusinessPhoneNumber",
                          ),
                        )}
                        required
                        endAdornment={
                          <MetaFieldHelp field="metaBusinessPhoneNumber" />
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const TelegramSection: React.FC = () => {
  const translate = useTranslate();
  const enabled = useWatch({ name: "telegram" });
  const { requiredFor } = useValidationMessages();

  return (
    <Card sx={{ border: 1, borderColor: "divider" }}>
      <CardContent
        sx={{
          pt: { xs: 1.5, sm: 3, lg: 0 },
          pb: { xs: 1.5, sm: 3, lg: 0 },
          pl: { xs: 1.5, sm: 3 },
          pr: { xs: 1.5, sm: 3 },
          mb: 0,
        }}
      >
        <ProviderToggleHeader
          source="telegram"
          title={translate("resources.providers.sections.telegram")}
        />
        <Box>
          {enabled && (
            <Box sx={panelSx}>
              <TextInput
                source="telegramProvider.botToken"
                label={translate("resources.providers.fields.botToken")}
                validate={required(requiredFor("telegramProvider.botToken"))}
                fullWidth
                InputProps={{
                  endAdornment: <ChannelFieldHelp id="telegram.botToken" />,
                }}
              />
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const ViberSection: React.FC = () => {
  const translate = useTranslate();
  const enabled = useWatch({ name: "viber" });
  const { requiredFor } = useValidationMessages();

  return (
    <Card sx={{ border: 1, borderColor: "divider" }}>
      <CardContent
        sx={{
          pt: { xs: 1.5, sm: 3, lg: 0 },
          pb: { xs: 1.5, sm: 3, lg: 0 },
          pl: { xs: 1.5, sm: 3 },
          pr: { xs: 1.5, sm: 3 },
          mb: 0,
        }}
      >
        <ProviderToggleHeader
          source="viber"
          title={translate("resources.providers.sections.viber")}
        />
        <Box>
          {enabled && (
            <Box sx={panelSx}>
              <TextInput
                source="viberProvider.botToken"
                label={translate("resources.providers.fields.viberAuthToken")}
                validate={required(requiredFor("viberProvider.botToken"))}
                fullWidth
                InputProps={{
                  endAdornment: <ChannelFieldHelp id="viber.botToken" />,
                }}
              />
              <TextInput
                source="viberProvider.senderName"
                label={translate("resources.providers.fields.viberSenderName")}
                helperText={translate("resources.providers.help.viberSenderName")}
                validate={required(requiredFor("viberProvider.senderName"))}
                inputProps={{ maxLength: 28 }}
                fullWidth
                InputProps={{
                  endAdornment: <ChannelFieldHelp id="viber.senderName" />,
                }}
              />
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const LineSection: React.FC = () => {
  const translate = useTranslate();
  const enabled = useWatch({ name: "line" });
  const { requiredFor } = useValidationMessages();

  return (
    <Card sx={{ border: 1, borderColor: "divider" }}>
      <CardContent
        sx={{
          pt: { xs: 1.5, sm: 3, lg: 0 },
          pb: { xs: 1.5, sm: 3, lg: 0 },
          pl: { xs: 1.5, sm: 3 },
          pr: { xs: 1.5, sm: 3 },
          mb: 0,
        }}
      >
        <ProviderToggleHeader
          source="line"
          title={translate("resources.providers.sections.line")}
        />
        <Box>
          {enabled && (
            <Box sx={panelSx}>
              <TextInput
                source="lineProvider.channelAccessToken"
                label={translate("resources.providers.fields.lineChannelAccessToken")}
                validate={required(requiredFor("lineProvider.channelAccessToken"))}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <ChannelFieldHelp id="line.channelAccessToken" />
                  ),
                }}
              />
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const PushSection: React.FC = () => {
  const translate = useTranslate();
  const enabled = useWatch({ name: "push" });
  const existingFileContain = useWatch({
    name: "webAppProviderModel.fileContain",
  });
  const replace = useWatch({ name: "replacePushFile" });
  const isEdit = useIsEditMode();
  const { requiredMsg } = useValidationMessages();

  const hasExistingFile = isEdit && Boolean(existingFileContain);
  const showFileInput = !hasExistingFile || Boolean(replace);

  return (
    <Card sx={{ border: 1, borderColor: "divider" }}>
      <CardContent
        sx={{
          pt: { xs: 1.5, sm: 3, lg: 0 },
          pb: { xs: 1.5, sm: 3, lg: 0 },
          pl: { xs: 1.5, sm: 3 },
          pr: { xs: 1.5, sm: 3 },
          mb: 0,
        }}
      >
        <ProviderToggleHeader
          source="push"
          title={translate("resources.providers.sections.push")}
        />
        <Box>
          {enabled && (
            <Box sx={panelSx}>
              {hasExistingFile && (
                <>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {translate("resources.providers.push.existingFileNotice")}
                  </Typography>
                  <BooleanInput
                    source="replacePushFile"
                    label={translate("resources.providers.push.replaceToggle")}
                    helperText={false}
                  />
                </>
              )}
              {showFileInput && (
                <FileInput
                  source="webAppProviderModel.uploadFile"
                  label={translate("resources.providers.fields.uploadFile")}
                  accept={{ "application/json": [".json"] }}
                  multiple={false}
                  validate={required(requiredMsg("pushNotificationFile"))}
                >
                  <FileField source="src" title="title" />
                </FileInput>
              )}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const ProviderToolbar: React.FC<{ resource?: string }> = ({ resource }) => {
  const record = useRecordContext<ProviderFormValues>();
  const email = useWatch({ name: "email" });
  const sms = useWatch({ name: "sms" });
  const whatsApp = useWatch({ name: "whatsApp" });
  const telegram = useWatch({ name: "telegram" });
  const viber = useWatch({ name: "viber" });
  const line = useWatch({ name: "line" });
  const push = useWatch({ name: "push" });
  const teamChat = useWatch({ name: "teamChat" });
  const anyTeamChat = TEAM_CHAT_PLATFORMS.some(
    (p) => teamChat?.[p.key]?.enabled,
  );
  const hasCommunicationType =
    (email && email !== "none") ||
    sms ||
    whatsApp ||
    telegram ||
    viber ||
    line ||
    push ||
    anyTeamChat ||
    // Edit mode: an existing provider (even with all channels blocked) can be saved.
    Boolean(record?.id);
  return <MyToolbar resource={resource} saveDisabled={!hasCommunicationType} />;
};

const TeamChatSection: React.FC<{ platform: { key: string; label: string } }> = ({
  platform,
}) => {
  const translate = useTranslate();
  const isEdit = useIsEditMode();
  const enabled = useWatch({ name: `teamChat.${platform.key}.enabled` });

  return (
    <Card sx={{ border: 1, borderColor: "divider" }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
        <ProviderToggleHeader
          source={`teamChat.${platform.key}.enabled`}
          title={platform.label}
        />
        <Box>
          {enabled && (
            <Box sx={panelSx}>
              <TextInput
                source={`teamChat.${platform.key}.webhookUrl`}
                label={translate("resources.providers.fields.webhookUrl")}
                validate={isEdit ? undefined : required()}
                helperText={
                  isEdit
                    ? translate("resources.providers.help.sensitiveOnEdit")
                    : translate("resources.providers.help.teamChatWebhook")
                }
                fullWidth
                InputProps={{
                  endAdornment: (
                    <ChannelFieldHelp id={`teamchat.${platform.key}`} />
                  ),
                }}
              />
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

/** Left rail (all possible transports, configured vs not) + right detail panel. */
const ProviderRailLayout: React.FC = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selected, setSelected] = React.useState<string>("general");

  const watched = useWatch({
    name: [
      "email",
      "sms",
      "whatsApp",
      "telegram",
      "viber",
      "line",
      "push",
      "teamChat",
    ],
  }) as [
    string | undefined,
    boolean | undefined,
    boolean | undefined,
    boolean | undefined,
    boolean | undefined,
    boolean | undefined,
    boolean | undefined,
    Record<string, { enabled?: boolean; present?: boolean }> | undefined,
  ];
  const [email, sms, whatsApp, telegram, viber, line, push, teamChat] = watched;

  // A channel counts as configured in the rail if it is active OR present
  // (configured but blocked) — a blocked channel must not vanish from the rail.
  const record = useRecordContext<ProviderFormValues>();
  const present = (m?: { id?: string }): boolean => Boolean(m?.id);
  const waConfigured =
    Boolean(whatsApp) ||
    present(record?.whatsappProvider) ||
    present(record?.whatsappMetaProvider);

  const groups: ProviderRailGroup[] = [
    {
      items: [
        {
          key: "general",
          title: translate("resources.providers.rail.general"),
          configured: true,
        },
      ],
    },
    {
      label: translate("resources.providers.rail.email"),
      items: [
        {
          key: "email",
          title: "Email",
          configured: Boolean(email) && email !== "none",
        },
      ],
    },
    {
      label: translate("resources.providers.rail.messaging"),
      collapsible: true,
      items: [
        { key: "whatsapp", title: "WhatsApp", configured: waConfigured },
        {
          key: "sms",
          title: "SMS",
          configured: Boolean(sms) || present(record?.smsProvider),
        },
        {
          key: "telegram",
          title: "Telegram",
          configured: Boolean(telegram) || present(record?.telegramProvider),
        },
        {
          key: "viber",
          title: "Viber",
          configured: Boolean(viber) || present(record?.viberProvider),
        },
        {
          key: "line",
          title: "LINE",
          configured: Boolean(line) || present(record?.lineProvider),
        },
      ],
    },
    {
      label: translate("resources.providers.rail.push"),
      items: [
        {
          key: "push",
          title: translate("resources.providers.rail.push"),
          configured: Boolean(push) || present(record?.webAppProviderModel),
        },
      ],
    },
    {
      label: translate("resources.providers.rail.teamChat"),
      collapsible: true,
      items: TEAM_CHAT_PLATFORMS.map((p) => ({
        key: `teamchat.${p.key}`,
        title: p.label,
        configured: Boolean(
          teamChat?.[p.key]?.enabled || teamChat?.[p.key]?.present,
        ),
      })),
    },
  ];

  const panel = (key: string, node: React.ReactNode): React.ReactNode => (
    <Box key={key} sx={{ display: selected === key ? "block" : "none" }}>
      {node}
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        width: "100%",
        minHeight: { sm: 440 },
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <ProviderRail groups={groups} selected={selected} onSelect={setSelected} />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          p: { xs: 1.5, sm: 2.5 },
          overflowY: "auto",
          maxHeight: isMobile ? undefined : "65vh",
        }}
      >
        {panel("general", <BasicSection />)}
        {panel("email", <EmailSection />)}
        {panel("whatsapp", <WhatsAppSection />)}
        {panel("sms", <SmsSection />)}
        {panel("telegram", <TelegramSection />)}
        {panel("viber", <ViberSection />)}
        {panel("line", <LineSection />)}
        {panel("push", <PushSection />)}
        {TEAM_CHAT_PLATFORMS.map((p) =>
          panel(`teamchat.${p.key}`, <TeamChatSection platform={p} />),
        )}
      </Box>
    </Box>
  );
};

export const ProviderForm: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const resource = useResourceContext();

  return (
    <SimpleForm
      toolbar={<ProviderToolbar resource={resource} />}
      mode="all"
      reValidateMode="onChange"
      criteriaMode="all"
      shouldFocusError
      sx={{
        display: "flex",
        flexDirection: "column",
        pb: isMobile ? 10 : 0,
      }}
    >
      <ProviderRailLayout />
    </SimpleForm>
  );
};

export default ProviderForm;
